import React, { type FunctionComponent, useMemo } from 'react'
import styled from '@emotion/styled'
import { FormattedMessage } from 'react-intl'
import { colors } from '@retailer-platform/shared-components'
import { SVGIcon, Text } from 'ic-snacks'
import { type StaffingLevelData } from 'domains/shifts/components/components/staffing-levels/utils/types'

interface StaffingLevelCellProps extends StaffingLevelData {
  isWeeklyView?: boolean
}

enum AllocationLevel {
  None = -1,
  Optimal = 0,
  UnderHigh,
  UnderMedium,
  UnderLow,
  OverLow,
  OverMedium,
  OverHigh,
}

const ALLOCATION_LEVEL_COLORS = {
  [AllocationLevel.None]: '#E8E9EB',
  [AllocationLevel.Optimal]: '#DAF3DA',
  [AllocationLevel.UnderHigh]: '#E29E9E',
  [AllocationLevel.UnderMedium]: '#EFC4C4',
  [AllocationLevel.UnderLow]: '#EFDBDB',
  [AllocationLevel.OverLow]: '#F8EADD',
  [AllocationLevel.OverMedium]: '#F2DAC2',
  [AllocationLevel.OverHigh]: '#EEC9A7',
}

const StaffingLevelBase = styled.div(
  ({ staffingLevel, isWeeklyView }: { staffingLevel: AllocationLevel; isWeeklyView: boolean }) => ({
    height: '100%',
    display: 'flex',
    flex: '1',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: ALLOCATION_LEVEL_COLORS[staffingLevel],
    color: staffingLevel === AllocationLevel.None ? colors.GRAYSCALE.X50 : colors.GRAYSCALE.X0,
    borderRight: isWeeklyView
      ? `2px solid ${colors.GRAYSCALE.X0}`
      : `1px solid ${colors.GRAYSCALE.X20}`,
  })
)

export const StaffingLevelCell: FunctionComponent<StaffingLevelCellProps> = ({
  required,
  scheduled,
  closed,
  filler,
  isWeeklyView,
}) => {
  const allocationLevel = useMemo(() => {
    if (required === null || closed) {
      return AllocationLevel.None
    }

    if (required === 0 && scheduled === 0) {
      return AllocationLevel.Optimal
    }

    const ratio = scheduled / required
    const difference = scheduled - required

    if (ratio < 0.34) {
      return AllocationLevel.UnderHigh
    }

    if (ratio < 0.67) {
      return AllocationLevel.UnderMedium
    }

    if (ratio < 1) {
      return AllocationLevel.UnderLow
    }

    if (ratio === 1) {
      return AllocationLevel.Optimal
    }

    if (difference <= 1) {
      return AllocationLevel.OverLow
    }

    if (difference < 3) {
      return AllocationLevel.OverMedium
    }

    if (difference >= 3) {
      return AllocationLevel.OverHigh
    }
  }, [required, scheduled, closed])

  const StaffingLevelContent = useMemo(() => {
    if (required === null) {
      return (
        <Text
          data-testid="shopper-shifts-staffing-unknown-requirements"
          variant={isWeeklyView ? 'T.14' : 'T.12'}
          fontWeight="semiBold"
          style={{ color: colors.GRAYSCALE.X70 }}
        >
          <FormattedMessage
            id="shiftsDomain.staffing_level"
            values={{ scheduled, required: '–' }}
          />
        </Text>
      )
    }

    if (filler) {
      return (
        <SVGIcon
          data-testid="shopper-shifts-staffing-filler"
          name="minus"
          color={colors.GRAYSCALE.X70}
          size="small"
        />
      )
    }

    if (closed) {
      return (
        <SVGIcon
          data-testid="shopper-shifts-staffing-closed"
          name="clock"
          color={colors.GRAYSCALE.X70}
          size="small"
        />
      )
    }

    const testid =
      allocationLevel === AllocationLevel.Optimal
        ? 'shopper-shifts-staffing-fully-staffed'
        : allocationLevel <= AllocationLevel.UnderLow
        ? 'shopper-shifts-staffing-partially-staffed'
        : 'shopper-shifts-staffing-over-staffed'

    const color =
      allocationLevel === AllocationLevel.Optimal
        ? '#177d05'
        : allocationLevel <= AllocationLevel.UnderLow
        ? '#AE1D1C'
        : '#BB5C05'

    return (
      <Text
        data-testid={testid}
        variant={isWeeklyView ? 'T.14' : 'T.12'}
        fontWeight="semiBold"
        style={{ color }}
      >
        <FormattedMessage
          id="shiftsDomain.staffing_level"
          values={{ scheduled, required: Math.round(required) }}
        />
      </Text>
    )
  }, [scheduled, required, closed, filler, isWeeklyView, allocationLevel])

  return (
    <StaffingLevelBase staffingLevel={allocationLevel} isWeeklyView={isWeeklyView}>
      {StaffingLevelContent}
    </StaffingLevelBase>
  )
}
